/**
 * Utility functions for exporting chat responses into CSV, PDF, and Markdown formats.
 */
import { marked } from 'marked';

const getFilename = (prefix: string, extension: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `${prefix}_${timestamp}.${extension}`;
};

const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const cleanCellText = (raw: string): string => {
  return raw
    .trim()
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/"/g, '""');
};

export const hasMarkdownTable = (markdownContent: string): boolean => {
  const tableLines = markdownContent
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.startsWith('|') && l.endsWith('|'));
  return tableLines.length >= 2;
};

export const exportToCSV = (markdownContent: string, title: string = 'ael_data_export') => {
  const lines = markdownContent.split('\n');
  const tableLines = lines
    .map(line => line.trim())
    .filter(line => line.startsWith('|'));

  let csvContent = '';

  if (tableLines.length >= 2) {
    const rows = tableLines
      // Skip markdown separator rows like |---|---| or | :--- | ---: |
      .filter(line => !/^\|[\s:|-]+\|?$/.test(line))
      .map(line => {
        const rawParts = line.split('|');
        const cells = rawParts
          .slice(1, rawParts[rawParts.length - 1].trim() === '' ? -1 : rawParts.length)
          .map(cell => `"${cleanCellText(cell)}"`);
        return cells.join(',');
      });
    csvContent = rows.join('\n');
  } else {
    csvContent = 'Property,Value\n' + lines
      .filter(line => line.trim().length > 0)
      .map(line => {
        const parts = line.replace(/^[*-]\s*/, '').split(':');
        if (parts.length >= 2) {
          const key = cleanCellText(parts[0]);
          const val = cleanCellText(parts.slice(1).join(':'));
          return `"${key}","${val}"`;
        }
        return `"${cleanCellText(line)}",""`;
      })
      .join('\n');
  }

  downloadFile('\uFEFF' + csvContent, getFilename(title, 'csv'), 'text/csv;charset=utf-8;');
};

export const exportToMarkdown = (markdownContent: string, title: string = 'ael_chat_report') => {
  downloadFile(markdownContent, getFilename(title, 'md'), 'text/markdown;charset=utf-8;');
};

export const exportToPDF = (markdownContent: string, title: string = 'Armellini Data Report') => {
  const printWindow = window.open('', '_blank', 'width=960,height=900');
  if (!printWindow) return;

  let renderedHtml = '';
  try {
    renderedHtml = marked.parse(markdownContent) as string;
  } catch {
    renderedHtml = markdownContent;
  }

  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            margin: 36px;
            color: #1e293b;
            line-height: 1.5;
          }
          .header {
            border-bottom: 2px solid #0284c7;
            padding-bottom: 12px;
            margin-bottom: 24px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .header h1 {
            margin: 0;
            color: #0f172a;
            font-size: 20px;
          }
          .header .meta {
            font-size: 12px;
            color: #64748b;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 16px 0;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #cbd5e1;
            padding: 8px 10px;
            text-align: left;
            vertical-align: top;
          }
          th {
            background-color: #f1f5f9;
            font-weight: 700;
            color: #0f172a;
          }
          tr:nth-child(even) td {
            background-color: #f8fafc;
          }
          pre {
            background: #f8fafc;
            padding: 12px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            overflow-x: auto;
          }
          .footer {
            margin-top: 40px;
            padding-top: 12px;
            border-top: 1px solid #e2e8f0;
            font-size: 11px;
            color: #94a3b8;
            text-align: center;
          }
          @media print {
            body { margin: 16px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>Armellini Express Lines — AI Data Report</h1>
            <div class="meta">Generated on ${new Date().toLocaleString()}</div>
          </div>
        </div>
        <div class="content">
          ${renderedHtml}
        </div>
        <div class="footer">
          Confidential — Armellini Logistics & CRM Internal System
        </div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
};
